import React from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, Toolbar, ResponsiveTable, TableSkeleton, EmptyState, ReasonModal, Modal, DateCell, StatusBadge } from "@/admin/ui";
import { RefreshCw, Check, X, Eye } from "lucide-react";

export default function AdminWebhooks() {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [processed, setProcessed] = React.useState("");
  const [type, setType] = React.useState("");
  const [detail, setDetail] = React.useState(null);
  const [confirmId, setConfirmId] = React.useState(null);

  const load = React.useCallback(() => {
    const params = {};
    if (processed !== "") params.processed = processed === "true";
    if (type) params.event_type = type;
    api.get("/api/admin/webhooks", { params }).then((r) => setItems(r.data.items));
  }, [processed, type]);
  React.useEffect(() => { load(); }, [load]);

  const doRetry = async (reason) => {
    await api.post(`/api/admin/webhooks/${confirmId}/retry`, { reason });
    toast.success(t("admin.webhooks.retry_requested"));
    load();
  };
  const view = async (id) => {
    const r = await api.get(`/api/admin/webhooks/${id}`);
    setDetail(r.data);
  };

  return (
    <div className="space-y-6" data-testid="admin-webhooks-page">
      <PageHeader title={t("admin.webhooks.title")} description={t("admin.webhooks.subtitle")} />
      <Toolbar right={<button onClick={load} className="lp-btn-ghost" aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>}>
        <select value={processed} onChange={(e) => setProcessed(e.target.value)} className="lp-input text-xs py-1.5" data-testid="filter-processed">
          <option value="">{t("admin.webhooks.filter.all")}</option>
          <option value="false">{t("admin.webhooks.filter.failed")}</option>
          <option value="true">{t("admin.webhooks.filter.processed")}</option>
        </select>
        <input value={type} onChange={(e) => setType(e.target.value)}
          placeholder={t("admin.webhooks.filter.type")}
          className="lp-input text-xs py-1.5" data-testid="filter-type" />
      </Toolbar>

      {!items ? <TableSkeleton rows={6} cols={7} /> : items.length === 0 ? (
        <EmptyState title="No webhook events match the current filters" />
      ) : (
        <ResponsiveTable minWidth={900}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("admin.webhooks.col.at")}</th>
            <th>{t("admin.webhooks.col.type")}</th>
            <th>{t("admin.webhooks.col.id")}</th>
            <th>{t("admin.webhooks.col.verified")}</th>
            <th>{t("admin.webhooks.col.processed")}</th>
            <th>{t("admin.webhooks.col.error")}</th>
            <th className="text-right"></th>
          </tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.event_id} data-testid={`webhook-${e.event_id}`}>
                <td><DateCell value={e.received_at} /></td>
                <td className="text-white/85 font-mono text-xs">{e.event_type}</td>
                <td className="text-white/60 font-mono text-xs">{e.event_id?.slice(0, 12)}…</td>
                <td>{e.verified ? <StatusBadge tone="success" icon={Check}>yes</StatusBadge> : <StatusBadge tone="danger" icon={X}>no</StatusBadge>}</td>
                <td>{e.processed ? <StatusBadge tone="success" icon={Check}>yes</StatusBadge> : <StatusBadge tone="warning" icon={X}>failed</StatusBadge>}</td>
                <td className="text-red-300 text-xs max-w-[240px] truncate" title={e.error}>{(e.error || "—").slice(0, 80)}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="lp-btn-ghost" onClick={() => view(e.event_id)} data-testid={`webhook-view-${e.event_id}`} aria-label="View"><Eye className="w-3.5 h-3.5" /></button>
                  {!e.processed && (
                    <button className="lp-btn-ghost" onClick={() => setConfirmId(e.event_id)} data-testid={`webhook-retry-${e.event_id}`}>
                      {t("admin.webhooks.retry")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      <Modal open={!!detail} onClose={() => setDetail(null)} size="lg"
        title={t("admin.webhooks.detail")}
        description={detail?.event_id}
        testid="webhook-detail-modal"
        footer={<button className="lp-btn-secondary" onClick={() => setDetail(null)}>Close</button>}>
        {detail && <>
          <div className="text-xs text-white/70 space-y-1">
            <div>Type: <span className="font-mono">{detail.event_type}</span></div>
            <div>Received: <DateCell value={detail.received_at} /></div>
            <div>Verified: {detail.verified ? "yes" : "no"} · Processed: {detail.processed ? "yes" : "no"}</div>
            {detail.error && <div className="text-red-300">Error: {detail.error}</div>}
          </div>
          <pre className="bg-black/40 rounded-lg p-3 text-[10px] text-white/80 overflow-x-auto max-h-72">
            {JSON.stringify(detail.raw_summary || {}, null, 2)}
          </pre>
          <div className="text-[11px] text-white/40">{t("admin.webhooks.redaction_notice")}</div>
        </>}
      </Modal>

      <ReasonModal open={!!confirmId} title={t("admin.webhooks.confirm_retry")}
        description="This will requeue the webhook for reprocessing. Completed idempotent replays return the original result — duplicate financial records will not be created."
        actionLabel={t("admin.webhooks.retry")} onClose={() => setConfirmId(null)} onSubmit={doRetry} minLen={3} />
    </div>
  );
}
